import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { Box, Typography } from '@mui/material';
import { formatNumber, NumberFormatPreset, QuickViewWidget, useScrollIntoView } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { CubeChart, CubeChartType } from '../../components/CubeChart';
import { I18nNamespace } from '~/common/types';
import { monthsInYearFormatter } from '~/components/CubeChart/CubeChart.common';
import { useModuleContext } from '~/components/ModuleContextProvider';
import { PlaceholderWidget } from '~/components/PlaceholderWidget/PlaceholderWidget';
import { getSegments, lastYearDateRange } from '~/util/cube';

const formatValueCompact = formatNumber(NumberFormatPreset.INTEGER_COMPACT);
const formatValue = formatNumber(NumberFormatPreset.INTEGER);

export const ActiveContractsQuickViewWidget = () => {
  const { entityId } = useModuleContext();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'ActiveContractsQuickViewWidget' });
  const [widgetRef, active] = useScrollIntoView({ hash: 'active-contracts-quickview' });

  const cube = 'MonthlyActiveContracts';
  const measure = `${cube}.count`;

  const filters: Filter[] = [
    {
      member: `${cube}.supplierId`,
      operator: 'equals',
      values: [entityId],
    },
  ];

  const queryValue = useCubeQuery({
    measures: [measure],
    timeDimensions: [
      {
        dimension: `${cube}.series`,
        dateRange: 'This month',
      },
    ],
    filters,
  });

  const current: number = queryValue.resultSet?.totalRow()[measure] ?? 0;

  const querySeries = useCubeQuery({
    measures: [measure],
    timeDimensions: [
      {
        dimension: `${cube}.series`,
        granularity: 'month',
        dateRange: lastYearDateRange,
      },
    ],
    filters,
  });

  const { segments } = getSegments(querySeries);

  return (
    <QuickViewWidget
      ref={widgetRef}
      active={active}
      loading={queryValue.isLoading || querySeries.isLoading}
      title={t('title')}
      subTitle={t('subTitle')}
      value={formatValueCompact(current)}
      valueTooltip={formatValue(current)}
      data-intercom-target="ActiveContractsQuickViewWidget"
    >
      {segments ? (
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 0',
            alignItems: 'flex-end',
          }}
        >
          <Box
            sx={{
              flex: '1 1 0',
              display: 'flex',
              mb: '-5px',
              mr: 3,
            }}
          >
            <Typography component="div" variant="caption" color="textPrimary">
              {t('chartCaption')}
            </Typography>
          </Box>
          <CubeChart
            type={CubeChartType.tinyBar}
            formatter={{
              x: monthsInYearFormatter,
              y: formatValue,
            }}
            {...querySeries}
          />
        </Box>
      ) : (
        <PlaceholderWidget />
      )}
    </QuickViewWidget>
  );
};
